import React, { useState } from 'react';
import type { PutSchoolImpactReportScheduleRequestBody, SchoolImpactReportSchedule } from '@whizz/api-model';
import type { OptionType } from '../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { Button, ButtonIcon, DatePicker, Input, Selector, Toggle } from '..';
import { GLOBAL_ICONS } from '../../util/vars';
import classNames from 'classnames';
import { formatDateForDisplay } from '../../util/dates';

interface Props {
  readOnly?: boolean;
  schedule: SchoolImpactReportSchedule;
  onSaveSchedule: (patch: PutSchoolImpactReportScheduleRequestBody) => void;
  onAddOneOffDate: (date: string, note?: string) => void;
  onDeleteOneOffDate: (id: number) => void;
  onToggleSkip: (occurrenceKey: string, skipped: boolean) => void;
  recipientOptions: OptionType<number>[];
  // undefined while recipients are loading.
  recipientIds: number[] | undefined;
  onSaveRecipients: (teacherIds: number[]) => void;
}

const LEAD_DAYS_OPTIONS = [ 3, 7, 14, 21 ];
const ANNUAL_LEAD_DAYS_OPTIONS = [ 7, 14, 28 ];
const FREQUENCY_OPTIONS: SchoolImpactReportSchedule['frequency'][] = [ 'half_term', 'full_term', 'month' ];

type OccurrenceStatus = SchoolImpactReportSchedule['upcomingOccurrences'][number]['status'];

const STATUS_DOT: Record<OccurrenceStatus, string> = {
  scheduled: 'bg-primary',
  sent: 'bg-positive',
  awaiting_review: 'bg-warning',
  failed: 'bg-negative',
};

const GENERATED_STATUSES: OccurrenceStatus[] = [ 'sent', 'awaiting_review', 'failed' ];

const ImpactReportScheduleFields = ({
  readOnly = false,
  schedule,
  onSaveSchedule,
  onAddOneOffDate,
  onDeleteOneOffDate,
  onToggleSkip,
  recipientOptions,
  recipientIds,
  onSaveRecipients,
}: Props): JSX.Element => {
  const { translate: t } = useI18n();
  const [ newOneOffDate, setNewOneOffDate ] = useState<Date | null>(null);
  const [ newOneOffNote, setNewOneOffNote ] = useState('');

  const leadDaysOptions: OptionType<number>[] = LEAD_DAYS_OPTIONS.map((days) => ({
    label: t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.DAYS_BEFORE', { days }),
    value: days,
  }));

  const annualLeadDaysOptions: OptionType<number>[] = ANNUAL_LEAD_DAYS_OPTIONS.map((days) => ({
    label: t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.DAYS_BEFORE', { days }),
    value: days,
  }));

  const frequencyOptions: OptionType<string>[] = FREQUENCY_OPTIONS.map((frequency) => ({
    label: t(`pages.ADMIN.IMPACT_REPORTS.SCHEDULE.FREQUENCY.${frequency.toUpperCase()}`),
    value: frequency,
  }));

  return (
    <div className={'grid grid-cols-5 gap-5'}>
      <div className={'col-span-3 space-y-5 border border-gray-200 rounded-lg p-5'}>

        <div className={'flex items-center gap-3'}>
          <ToggleWithLabel
            disabled={readOnly}
            id={'isEnabled'}
            label={t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.ENABLED')}
            checked={schedule.isEnabled}
            onChange={(e) => onSaveSchedule({ isEnabled: e.target.checked })}
          />
        </div>

        <div className={'pb-5 border-b border-gray-200 space-y-2'}>
          <Selector
            id={'recipients'}
            label={t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SEND_TO')}
            placeholder={t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SEND_TO_PLACEHOLDER')}
            options={recipientOptions}
            value={recipientOptions.filter((option) => (recipientIds ?? []).includes(option.value))}
            onChange={(options: OptionType<number>[] | null) => onSaveRecipients((options ?? []).map((option) => option.value))}
            disabled={readOnly}
            isMulti
          />
          <div className={'text-gray-400 text-sm'}>{t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SEND_TO_HINT')}</div>
          {schedule.isEnabled && recipientIds && 0 === recipientIds.length && (
            <p role={'alert'} className={'text-negative text-sm'}>{t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SEND_TO_NONE')}</p>
          )}
        </div>

        <div className={'flex flex-wrap items-center gap-3'}>
          <span>{t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SEND_ME_A_REPORT')}</span>
          <div className={'w-28'}>
            <Selector
              size={'sm'}
              disabled={readOnly}
              value={leadDaysOptions.find((option) => option.value === schedule.leadDays) ?? null}
              onChange={(option: OptionType<number>) => onSaveSchedule({ leadDays: Number(option.value) })}
              options={leadDaysOptions}
              isClearable={false}
            />
          </div>
          <div className={'flex items-center gap-3'}>
            <span>{t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.BEFORE_THE_END_OF_EACH')}</span>
            <div className={'w-40'}>
              <Selector
                size={'sm'}
                disabled={readOnly}
                value={frequencyOptions.find((option) => option.value === schedule.frequency) ?? null}
                onChange={(option: OptionType<string>) => onSaveSchedule({ frequency: option.value as SchoolImpactReportSchedule['frequency'] })}
                options={frequencyOptions}
                isClearable={false}
              />
            </div>
          </div>
        </div>

        <div className={'flex items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50'}>
          <Toggle
            disabled={readOnly}
            id={'annualReportEnabled'}
            label={t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.ANNUAL_REPORT')}
            color={'header'}
            checked={schedule.annualReportEnabled}
            isOnOff
            onChange={(e) => onSaveSchedule({ annualReportEnabled: e.target.checked })}
          />
          {/* One flowing sentence, so only the words that don't fit wrap rather than the whole phrase. */}
          <div className={'flex-1 min-w-0 leading-8'}>
            <span className={classNames({ 'text-gray-500': !schedule.annualReportEnabled, 'font-medium': schedule.annualReportEnabled })}>
              {t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.ANNUAL_REPORT')}
            </span>
            <div className={'inline-block w-28 mx-2 align-middle'}>
              <Selector
                size={'sm'}
                value={annualLeadDaysOptions.find((option) => option.value === schedule.annualReportLeadDays) ?? null}
                onChange={(option: OptionType<number>) => onSaveSchedule({ annualReportLeadDays: Number(option.value) })}
                options={annualLeadDaysOptions}
                disabled={readOnly || !schedule.annualReportEnabled}
                isClearable={false}
                hideLabel
              />
            </div>
            <span className={classNames({ 'text-gray-500': !schedule.annualReportEnabled })}>
              {t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.BEFORE_YEAR_ENDS')}
            </span>
          </div>
        </div>
      </div>

      <div className={'col-span-2 space-y-2 border border-gray-200 rounded-lg p-5'}>
        <h4 className={'font-medium'}>
          {t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SCHEDULED_SENDS', { count: schedule.upcomingOccurrences.length })}
        </h4>
        {schedule.upcomingOccurrences.map((occurrence) => {
          const isScheduled = 'scheduled' === occurrence.status;
          const statusLabel = t(`pages.ADMIN.IMPACT_REPORTS.SCHEDULE.STATUS.${occurrence.status.toUpperCase()}`);

          return (
            <div key={occurrence.occurrenceKey} className={'flex items-center gap-3 bg-gray-50 border border-gray-100 rounded p-2'}>
              {isScheduled ? (
                <button
                  disabled={readOnly}
                  type={'button'}
                  aria-label={occurrence.skipped ? 'Unskip' : 'Skip'}
                  className={`flex-none w-3 h-3 rounded-full ${occurrence.skipped ? 'border border-gray-300' : STATUS_DOT.scheduled}`}
                  onClick={() => onToggleSkip(occurrence.occurrenceKey, !occurrence.skipped)}
                />
              ) : (
                // Once a report has been generated for a period it can no longer be skipped.
                <span role={'img'} aria-label={statusLabel} title={statusLabel} className={`flex-none w-3 h-3 rounded-full ${STATUS_DOT[occurrence.status]}`} />
              )}
              <span className={occurrence.skipped ? 'line-through text-gray-400' : 'font-medium'}>{formatDateForDisplay(occurrence.date)}</span>
              <span className={'ml-auto text-gray-500 text-sm'}>
                {occurrence.skipped ? t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SKIPPED') : `${occurrence.label}${isScheduled ? '' : ` · ${statusLabel}`}`}
              </span>
            </div>
          );
        })}
        {schedule.upcomingOccurrences.some((occurrence) => 'scheduled' !== occurrence.status) && (
          <div className={'flex flex-wrap gap-3 text-xs text-gray-500'}>
            {GENERATED_STATUSES.map((status) => (
              <span key={status} className={'flex items-center gap-1'}>
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
                {t(`pages.ADMIN.IMPACT_REPORTS.SCHEDULE.STATUS.${status.toUpperCase()}`)}
              </span>
            ))}
          </div>
        )}
        {!readOnly && <p className={'text-xs text-gray-400'}>{t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.SKIP_HINT')}</p>}
      </div>
    </div>
  );
};

const ToggleWithLabel = ({ id, label, checked, onChange, disabled }: { disabled?: boolean; id: string; label: string; checked: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => {
  return (
    <>
      <Toggle
        disabled={disabled}
        id={id}
        label={label}
        color={'header'}
        checked={checked}
        isOnOff
        onChange={onChange}
      />
      <span className={classNames({ 'text-gray-400': !checked, 'font-medium': checked })}>{label}</span>
    </>
  );
};

export { ImpactReportScheduleFields };
